/**
 * Sentry bootstrap — errors, tracing, session replay and structured logs.
 *
 * Imported before any other app module from `src/index.ts` so the SDK is installed
 * before the first line of feature code runs.
 *
 * Day-to-day logging goes through `@root/app/shared/new-services/logging.service`,
 * which documents the conventions we follow at call sites.
 */
import * as Sentry from '@sentry/react';
import type { Log } from '@sentry/react';
import { appSettings } from '@root/app/shared/config/settings';
import { HTML_AS_SCRIPT_PATTERN, isBundleUrl } from './chunkRecovery';

const isProduction = appSettings.env === 'production';

/**
 * Attribute keys that must never leave the browser, no matter what a call site passes.
 * Matched case-insensitively as substrings, so `sessionId`, `SessionID` and
 * `refresh_token` are all covered.
 *
 * This is the safety net — the real rule is "don't log secrets or PII at the call site".
 */
const SENSITIVE_ATTRIBUTE_PATTERNS = ['password', 'token', 'session', 'authorization', 'secret', 'credit'];

const isSensitiveKey = (key: string): boolean => {
    const lowered = key.toLowerCase();
    return SENSITIVE_ATTRIBUTE_PATTERNS.some((pattern) => lowered.includes(pattern));
};

/**
 * Third-party console noise, measured against a real pageload: MUI/emotion alone emits the
 * `:first-child` warning eight times before the login screen paints, and none of these are
 * ours to act on. Dropping them here rather than narrowing `consoleLoggingIntegration`
 * keeps genuine `console.warn`/`console.error` calls from our own code flowing.
 *
 * Add to this list when a dependency starts shouting; remove when you fix the underlying
 * warning.
 */
const THIRD_PARTY_LOG_NOISE = [
    'The pseudo class ":first-child" is potentially unsafe',
    'React Router Future Flag Warning',
    '[GSI_LOGGER]'
];

const isThirdPartyNoise = (message: string): boolean =>
    THIRD_PARTY_LOG_NOISE.some((noise) => message.includes(noise));

const scrubSensitiveAttributes = (log: Log): Log => {
    if (!log.attributes) {
        return log;
    }

    Object.keys(log.attributes).forEach((key) => {
        if (isSensitiveKey(key)) {
            log.attributes[key] = '[Filtered]';
        }
    });

    return log;
};

Sentry.init({
    dsn: 'https://291d0e1d18020f1f3e5ca9b192ee8369@o4506269099032576.ingest.sentry.io/4506676079099904',

    // Lets us filter issues, traces and logs per deploy target in the Sentry UI.
    environment: appSettings.env,

    integrations: [
        // Pageload/navigation spans. Logs emitted inside an active span are automatically
        // linked to it, which is what makes "jump from this log to the trace" work.
        //
        // `tracePropagationTargets` is deliberately left at its default (same-origin only),
        // so no `sentry-trace` / `baggage` headers are added to cross-origin API calls.
        // Widening it to the Happyforce API needs those headers allow-listed in CORS first.
        Sentry.browserTracingIntegration(),

        Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false
        }),

        // Existing `console.warn` / `console.error` calls become structured logs for free.
        // `log`/`info`/`debug`/`trace` are left out on purpose: the codebase uses
        // `console.trace` for local call stacks and forwarding those would drown the stream.
        Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] })
    ],

    // Structured logging is opt-in — without this every `Sentry.logger.*` call is a no-op
    // and `beforeSendLog` never fires.
    enableLogs: true,

    // 1.0 everywhere was harmless while tracing was inert; now that the integration is
    // actually installed, sample production down so we don't burn the quota.
    tracesSampleRate: isProduction ? 0.2 : 1.0,

    attachStacktrace: true,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Stale chunks after a deploy are handled by `setupChunkRecovery()`, which reloads the
    // page and reports explicitly (with a different message) when the reload doesn't fix
    // it. These loader messages are the symptom, not an actionable defect.
    ignoreErrors: [
        /Loading (?:CSS )?chunk [^\s]+ failed/i
    ],

    /**
     * Drop `Unexpected token '<'` only when it comes from one of our own bundles — that is
     * a stale chunk answered with index.html, which `setupChunkRecovery()` recovers from.
     * The same message raised anywhere else (parsing an HTML body as JSON, for instance) is
     * a real defect and must still be reported.
     */
    beforeSend: (event) => {
        const exception = event.exception?.values?.[0];
        if (!exception || !HTML_AS_SCRIPT_PATTERN.test(exception.value ?? '')) {
            return event;
        }

        const fromBundle = exception.stacktrace?.frames?.some((frame) => isBundleUrl(frame.filename));

        return fromBundle ? null : event;
    },

    beforeSendLog: (log: Log): Log | null => {
        // `trace` and `debug` are development diagnostics. Keeping them out of production
        // is the single biggest lever on log volume and cost.
        if (isProduction && (log.level === 'trace' || log.level === 'debug')) {
            return null;
        }

        if (typeof log.message === 'string' && isThirdPartyNoise(log.message)) {
            return null;
        }

        return scrubSensitiveAttributes(log);
    }
});
